<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Statistics Cards =========== -->
      <div class="statistics_cards_wrapper mb-4">
        <v-row>
          <!-- Total Announcements -->
          <v-col cols="12" md="6" lg="3">
            <div class="stat_card">
              <div class="stat_content">
                <h4>{{ statistics.announcements_count || 0 }}</h4>
                <p>{{ $t("STATISTICS.total_announcements") }}</p>
              </div>
            </div>
          </v-col>

          <!-- Published Announcements -->
          <v-col cols="12" md="6" lg="3">
            <div class="stat_card">
              <div class="stat_content">
                <h4>{{ statistics.announcements_published_count || 0 }}</h4>
                <p>{{ $t("STATISTICS.published_announcements") }}</p>
              </div>
            </div>
          </v-col>

          <!-- Finished Announcements -->
          <v-col cols="12" md="6" lg="3">
            <div class="stat_card">
              <div class="stat_content">
                <h4>{{ statistics.announcements_finished_count || 0 }}</h4>
                <p>{{ $t("STATISTICS.finished_announcements") }}</p>
              </div>
            </div>
          </v-col>

          <!-- Payment Failed Announcements -->
          <v-col cols="12" md="6" lg="3">
            <div class="stat_card">
              <div class="stat_content">
                <h4>{{ statistics.announcements_payment_fail_count || 0 }}</h4>
                <p>{{ $t("STATISTICS.payment_failed_announcements") }}</p>
              </div>
            </div>
          </v-col>
        </v-row>
      </div>
      <!--  =========== End:: Statistics Cards =========== -->

      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Ad Name Input -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.ads_name')"
                v-model.trim="filterOptions.name"
              />
              <!-- End:: Ad Name Input -->

              <!-- Start:: Brand Name Input -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.store_name')"
                v-model.trim="filterOptions.brand_name"
              />
              <!-- End:: Brand Name Input -->

              <!-- Start:: Number Input -->
              <base-input
                col="4"
                type="number"
                :placeholder="$t('PLACEHOLDERS.number')"
                v-model.trim="filterOptions.number"
              />
              <!-- End:: Number Input -->

              <!-- Start:: City Select -->
              <base-select-input
                col="4"
                :optionsList="cities"
                :placeholder="$t('PLACEHOLDERS.city')"
                v-model="filterOptions.city_id"
              />
              <!-- End:: City Select -->

              <!-- Start:: Ad Status Select -->
              <base-select-input
                col="4"
                :optionsList="adStatuses"
                :placeholder="$t('PLACEHOLDERS.ad_status')"
                v-model="filterOptions.status"
              />
              <!-- End:: Ad Status Select -->

              <!-- Start:: Payment Status Select -->
              <base-select-input
                col="4"
                :optionsList="paymentStatuses"
                :placeholder="$t('PLACEHOLDERS.payment_status')"
                v-model="filterOptions.payment_status"
              />
              <!-- End:: Payment Status Select -->

              <!-- Start:: Start Date Input -->
              <base-picker-input
                col="6"
                type="date"
                :placeholder="$t('PLACEHOLDERS.created_at')"
                v-model.trim="filterOptions.created_at"
              />
              <!-- End:: Start Date Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("PLACEHOLDERS.advertsManagement") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Serial Number -->
        <template v-slot:[`item.serial`]="{ index }">
          <div class="table_image_wrapper">
            <p>
              {{
                (paginations.current_page - 1) * paginations.items_per_page +
                index +
                1
              }}
            </p>
          </div>
        </template>
        <!-- End:: Serial Number -->

        <!-- Start:: Ad ID -->
        <template v-slot:[`item.id`]="{ item }">
          <p class="text-danger" v-if="!item.id">{{ $t("TABLES.noData") }}</p>
          <p v-else>#{{ item.id }}</p>
        </template>
        <!-- End:: Ad ID -->

        <!-- Start:: Ad Name -->
        <template v-slot:[`item.name`]="{ item }">
          <p class="text-danger" v-if="!item.name">{{ $t("TABLES.noData") }}</p>
          <p v-else>{{ item.name }}</p>
        </template>
        <!-- End:: Ad Name -->

        <!-- Start:: Store Name -->
        <template v-slot:[`item.store_brand_name`]="{ item }">
          <p class="text-danger" v-if="!item.store_brand_name">
            {{ $t("TABLES.noData") }}
          </p>
          <p v-else>{{ item.store_brand_name }}</p>
        </template>
        <!-- End:: Store Name -->

        <!-- Start:: Store Mobile -->
        <template v-slot:[`item.store_mobile`]="{ item }">
          <p class="text-danger" v-if="!item.store_mobile">
            {{ $t("TABLES.noData") }}
          </p>
          <p v-else>{{ item.store_mobile }}</p>
        </template>
        <!-- End:: Store Mobile -->

        <!-- Start:: City -->
        <template v-slot:[`item.city`]="{ item }">
          <p class="text-danger" v-if="!item.city">
            {{ $t("TABLES.noData") }}
          </p>
          <p v-else>
            {{ getAppLocale === "ar" ? item.city.name_ar : item.city.name_en }}
          </p>
        </template>
        <!-- End:: City -->

        <!-- Start:: Start Date -->
        <template v-slot:[`item.start_date`]="{ item }">
          <p class="text-danger" v-if="!item.dates || !item.dates.length">
            {{ $t("TABLES.noData") }}
          </p>
          <p v-else>{{ formatDate(item.dates[0].start_at) }}</p>
        </template>
        <!-- End:: Start Date -->

        <!-- Start:: Time Range -->
        <template v-slot:[`item.time_range`]="{ item }">
          <p class="text-danger" v-if="!item.dates || !item.dates.length">
            {{ $t("TABLES.noData") }}
          </p>
          <p v-else>
            {{ formatTime(item.dates[0].start_at) }} -
            {{ formatTime(item.dates[0].end_at) }}
          </p>
        </template>
        <!-- End:: Time Range -->

        <!-- Start:: Guests Number -->
        <template v-slot:[`item.guests_number`]="{ item }">
          <p
            class="text-danger"
            v-if="
              item.guests_number === null || item.guests_number === undefined
            "
          >
            {{ $t("TABLES.noData") }}
          </p>
          <p v-else>{{ item.guests_number }}</p>
        </template>
        <!-- End:: Guests Number -->

        <!-- Start:: Package Users Count -->
        <template v-slot:[`item.confirmed_users`]="{ item }">
          <p class="text-danger" v-if="!item.package">
            {{ $t("TABLES.noData") }}
          </p>
          <p v-else>{{ item.package.confirmed_users || 0 }}</p>
        </template>
        <!-- End:: Package Users Count -->

        <!-- End:: Ad Status -->

        <!-- End:: Payment Status -->

        <!-- Start:: Amount Paid -->
        <template v-slot:[`item.total_amount`]="{ item }">
          <p class="text-danger" v-if="!item.package_price">
            {{ $t("TABLES.noData") }}
          </p>
          <p v-else>
            {{ calculateTotal(item.package_price, item.tax_in_value) }} ر.س
          </p>
        </template>
        <!-- End:: Amount Paid -->

        <!-- Start:: Created At -->
        <template v-slot:[`item.created_at`]="{ item }">
          <p class="text-danger" v-if="!item.created_at">
            {{ $t("TABLES.noData") }}
          </p>
          <p v-else>{{ formatDate(item.created_at) }}</p>
        </template>
        <!-- End:: Created At -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showDetails(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>

            <!-- Toggle Active/Inactive (only for new, published, or rejected) -->
            <a-tooltip
              placement="bottom"
              v-if="
                item.status === 'new' ||
                item.status === 'published' ||
                item.status === 'rejected'
              "
            >
              <template slot="title">
                <span>{{
                  item.status === "published"
                    ? $t("BUTTONS.deactivate")
                    : $t("BUTTONS.activate")
                }}</span>
              </template>
              <button class="btn_edit" @click="selectToggleItem(item)">
                <i
                  :class="
                    item.status === 'published'
                      ? 'fal fa-toggle-on'
                      : 'fal fa-toggle-off'
                  "
                ></i>
              </button>
            </a-tooltip>

            <!-- Delete Button -->
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.delete") }}</span>
              </template>
              <button class="btn_delete" @click="selectDeleteItem(item)">
                <i class="fal fa-trash-alt"></i>
              </button>
            </a-tooltip>
          </div>
        </template>
        <!-- End:: Actions -->

        <!-- ======================== Start:: Dialogs ======================== -->
        <template v-slot:top>
          <!-- Start:: Details Modal -->
          <v-dialog v-model="dialogDetails" max-width="900px" scrollable>
            <v-card v-if="selectedAd">
              <v-card-title class="text-h5 text-center bg-primary white--text">
                {{ $t("PLACEHOLDERS.show_ads") }}
              </v-card-title>

              <v-card-text class="pa-5">
                <div class="details-section">
                  <!-- Ad Information -->
                  <h3 class="section-title mb-3">
                    {{ $t("PLACEHOLDERS.order_data") }}
                  </h3>
                  <v-row class="mb-4">
                    <v-col cols="12" md="6">
                      <div class="detail-item">
                        <strong>{{ $t("PLACEHOLDERS.ads_name") }}:</strong>
                        <span>{{
                          selectedAd.name || $t("TABLES.noData")
                        }}</span>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="detail-item">
                        <strong>{{ $t("PLACEHOLDERS.store_name") }}:</strong>
                        <span>{{
                          selectedAd.store_brand_name || $t("TABLES.noData")
                        }}</span>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="detail-item">
                        <strong>{{ $t("PLACEHOLDERS.phone") }}:</strong>
                        <span>{{
                          selectedAd.store_mobile || $t("TABLES.noData")
                        }}</span>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="detail-item">
                        <strong>{{ $t("PLACEHOLDERS.city") }}:</strong>
                        <span>{{
                          selectedAd.city
                            ? getAppLocale === "ar"
                              ? selectedAd.city.name_ar
                              : selectedAd.city.name_en
                            : $t("TABLES.noData")
                        }}</span>
                      </div>
                    </v-col>
                    <v-col cols="12">
                      <div class="detail-item">
                        <strong>{{ $t("PLACEHOLDERS.address") }}:</strong>
                        <span>{{
                          selectedAd.address || $t("TABLES.noData")
                        }}</span>
                      </div>
                    </v-col>
                    <v-col cols="12" v-if="selectedAd.Hospitality">
                      <div class="detail-item">
                        <strong>{{ $t("PLACEHOLDERS.desc") }}:</strong>
                        <span>{{ selectedAd.Hospitality }}</span>
                      </div>
                    </v-col>
                    <v-col cols="12" v-if="selectedAd.notes">
                      <div class="detail-item">
                        <strong>{{ $t("PLACEHOLDERS.note") }}:</strong>
                        <span>{{ selectedAd.notes }}</span>
                      </div>
                    </v-col>
                  </v-row>

                  <!-- Dates and Times -->
                  <h3 class="section-title mb-3">
                    {{ $t("PLACEHOLDERS.dates") }}
                  </h3>
                  <v-row class="mb-4">
                    <v-col
                      cols="12"
                      v-if="selectedAd.dates && selectedAd.dates.length"
                    >
                      <div
                        v-for="(date, index) in selectedAd.dates"
                        :key="index"
                        class="date-item mb-2"
                      >
                        <strong
                          >{{ $t("PLACEHOLDERS.from") }}
                          {{ index + 1 }}:</strong
                        >
                        <span
                          >{{ formatDate(date.start_at) }}
                          {{ $t("PLACEHOLDERS.from") }}
                          {{ formatTime(date.start_at) }} إلى
                          {{ formatTime(date.end_at) }}</span
                        >
                      </div>
                    </v-col>
                  </v-row>

                  <!-- Images -->
                  <h3 class="section-title mb-3">
                    {{ $t("PLACEHOLDERS.imagesefed") }}
                  </h3>
                  <v-row class="mb-4">
                    <v-col
                      cols="12"
                      md="4"
                      v-for="(image, index) in selectedAd.images"
                      :key="index"
                    >
                      <img
                        :src="image"
                        class="ad-image"
                        @click="showImageModal({ image })"
                      />
                    </v-col>
                  </v-row>

                  <!-- Package and Payment Information -->
                  <h3 class="section-title mb-3">
                    {{ $t("PLACEHOLDERS.priceData") }}
                  </h3>
                  <v-row class="mb-4">
                    <v-col cols="12" md="6">
                      <div class="detail-item">
                        <strong
                          >{{ $t("PLACEHOLDERS.number_of_people") }}:</strong
                        >
                        <span>{{
                          selectedAd.guests_number || $t("TABLES.noData")
                        }}</span>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="detail-item">
                        <strong>{{ $t("PLACEHOLDERS.package_name") }}:</strong>
                        <span>{{
                          selectedAd.package
                            ? selectedAd.package.name
                            : $t("TABLES.noData")
                        }}</span>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="detail-item">
                        <strong>{{ $t("PLACEHOLDERS.package_price") }}:</strong>
                        <span
                          >{{ selectedAd.package_price || "0" }}
                          {{ $t("PLACEHOLDERS.sar") }}</span
                        >
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="detail-item">
                        <strong>{{ $t("PLACEHOLDERS.tax") }}:</strong>
                        <span
                          >{{ selectedAd.tax_in_value || "0" }}
                          {{ $t("PLACEHOLDERS.sar") }}</span
                        >
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="detail-item">
                        <strong>{{ $t("PLACEHOLDERS.total_price") }}:</strong>
                        <span
                          >{{
                            calculateTotal(
                              selectedAd.package_price,
                              selectedAd.tax_in_value
                            )
                          }}
                          {{ $t("PLACEHOLDERS.sar") }}</span
                        >
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="detail-item">
                        <strong
                          >{{ $t("PLACEHOLDERS.transaction_id") }}:</strong
                        >
                        <span>{{
                          selectedAd.transaction_id || $t("TABLES.noData")
                        }}</span>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="detail-item">
                        <strong
                          >{{
                            $t("PLACEHOLDERS.payment_method_brand")
                          }}:</strong
                        >
                        <span
                          class="badge"
                          :class="{
                            'badge-success':
                              selectedAd.payemnt_status === 'successed',
                            'badge-danger':
                              selectedAd.payemnt_status === 'failed',
                            'badge-info':
                              selectedAd.payemnt_status === 'refunded',
                          }"
                        >
                          {{ selectedAd.payemnt_status_label }}
                        </span>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="detail-item">
                        <strong>{{ $t("PLACEHOLDERS.created_at") }}:</strong>
                        <span>{{
                          selectedAd.payed_at
                            ? formatDate(selectedAd.payed_at)
                            : $t("TABLES.noData")
                        }}</span>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="detail-item">
                        <strong
                          >{{ $t("PLACEHOLDERS.payment_method") }}:</strong
                        >
                        <span>{{
                          getPaymentMethodText(selectedAd.payemnt_method)
                        }}</span>
                      </div>
                    </v-col>
                  </v-row>

                  <!-- Engagement Metrics -->
                  <h3 class="section-title mb-3">
                    {{ $t("PLACEHOLDERS.rating") }}
                  </h3>
                  <v-row class="mb-4">
                    <v-col cols="12" md="4">
                      <div class="detail-item">
                        <strong
                          >{{ $t("PLACEHOLDERS.number_of_people") }}:</strong
                        >
                        <span>{{
                          selectedAd.package
                            ? selectedAd.package.users_count
                            : 0
                        }}</span>
                      </div>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="detail-item">
                        <strong
                          >{{
                            $t("PLACEHOLDERS.number_of_individuals")
                          }}:</strong
                        >
                        <span>{{ calculateActualAttendance(selectedAd) }}</span>
                      </div>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="detail-item">
                        <strong
                          >{{
                            $t("PLACEHOLDERS.discount_percentage_on_booking")
                          }}:</strong
                        >
                        <span
                          >{{ selectedAd.attendance_percantage || 0 }}%</span
                        >
                      </div>
                    </v-col>
                  </v-row>

                  <!-- Map Location -->
                  <h3 class="section-title mb-3">
                    {{ $t("PLACEHOLDERS.location") }}
                  </h3>
                  <v-row class="mb-4">
                    <v-col cols="12">
                      <div
                        class="map-container"
                        v-if="selectedAd.lat && selectedAd.lng"
                      >
                        <iframe
                          :src="`https://maps.google.com/maps?q=${selectedAd.lat},${selectedAd.lng}&z=15&output=embed`"
                          width="100%"
                          height="300"
                          frameborder="0"
                          style="border: 0"
                        ></iframe>
                      </div>
                      <p v-else class="text-danger">
                        {{ $t("TABLES.noData") }}
                      </p>
                    </v-col>
                  </v-row>
                </div>
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn class="modal_cancel_btn" @click="dialogDetails = false">
                  {{ $t("BUTTONS.close") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- End:: Details Modal -->

          <!-- Start:: Delete Modal -->
          <v-dialog v-model="dialogDelete" max-width="500px">
            <v-card>
              <v-card-title
                class="text-h5 justify-center transparent-bg"
                style="background-color: transparent !important"
                v-if="itemToDelete"
              >
                {{
                  $t("TITLES.DeleteConfirmingMessage", {
                    name: itemToDelete.name,
                  })
                }}
              </v-card-title>
              <v-card-actions>
                <v-btn class="modal_confirm_btn" @click="confirmDeleteItem">{{
                  $t("BUTTONS.ok")
                }}</v-btn>

                <v-btn class="modal_cancel_btn" @click="dialogDelete = false">{{
                  $t("BUTTONS.cancel")
                }}</v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- End:: Delete Modal -->

          <!-- Start:: Toggle Status Modal -->
          <v-dialog v-model="dialogToggle" max-width="500px">
            <v-card>
              <v-card-title class="text-h5 justify-center" v-if="itemToToggle">
                {{
                  itemToToggle.status === "published"
                    ? $t("MESSAGES.confirmDeactivate")
                    : $t("MESSAGES.confirmActivate")
                }}
              </v-card-title>
              <v-card-actions>
                <v-btn class="modal_confirm_btn" @click="confirmToggleStatus">{{
                  $t("BUTTONS.ok")
                }}</v-btn>

                <v-btn class="modal_cancel_btn" @click="dialogToggle = false">{{
                  $t("BUTTONS.cancel")
                }}</v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- End:: Toggle Status Modal -->

          <!-- Start:: Image Modal -->
          <image-modal
            v-if="dialogImage"
            type="image"
            :modalIsOpen="dialogImage"
            :modalImage="selectedItemImage"
            @toggleModal="dialogImage = !dialogImage"
          />
          <!-- End:: Image Modal -->
        </template>
        <!-- ======================== End:: Dialogs ======================== -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllAds",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    adStatuses() {
      return [
        {
          id: 1,
          name: this.$t("STATUS.pending"),
          value: "pending",
        },
        {
          id: 2,
          name: this.$t("STATUS.active"),
          value: "active",
        },
        {
          id: 3,
          name: this.$t("STATUS.inactive"),
          value: "inactive",
        },
        {
          id: 4,
          name: this.$t("STATUS.rejected"),
          value: "rejected",
        },
        {
          id: 5,
          name: this.$t("STATUS.published"),
          value: "published",
        },
        {
          id: 6,
          name: this.$t("STATUS.finished"),
          value: "finished",
        },
        {
          id: 7,
          name: this.$t("STATUS.new"),
          value: "new",
        },
        {
          id: 8,
          name: this.$t("STATUS.not_payed"),
          value: "not_payed",
        },
        {
          id: 9,
          name: this.$t("PLACEHOLDERS.payment_failed"),
          value: "payment_failed",
        },
      ];
    },

    paymentStatuses() {
      return [
        {
          id: 1,
          name: this.$t("STATUS.successed"),
          value: "successed",
        },
        {
          id: 2,
          name: this.$t("STATUS.failed"),
          value: "failed",
        },
        {
          id: 3,
          name: this.$t("STATUS.refund"),
          value: "refund",
        },
        {
          id: 4,
          name: this.$t("STATUS.not_payed"),
          value: "not_payed",
        },
      ];
    },
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Statistics Data
      statistics: {
        announcements_count: 0,
        announcements_published_count: 0,
        announcements_finished_count: 0,
        announcements_payment_fail_count: 0,
      },
      // End:: Statistics Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        name: null,
        brand_name: null,
        number: null,
        city_id: null,
        status: null,
        payment_status: null,
        created_at: null,
      },
      cities: [],
      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: "#",
          value: "serial",
          align: "center",
          width: "80",
          sortable: false,
        },
        {
          text: this.$t("TABLES.ad_number"),
          value: "id",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.ads_name"),
          value: "name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.store_name"),
          value: "store_brand_name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.phone"),
          value: "store_mobile",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.city"),
          value: "city",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.startDate"),
          value: "start_date",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.time_range"),
          value: "time_range",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.number_of_people"),
          value: "guests_number",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.confirmed_users"),
          value: "confirmed_users",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.order_status"),
          value: "status_label",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.paymentStatus"),
          value: "payemnt_status_label",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.total_price"),
          value: "total_amount",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.created_at"),
          value: "created_at",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.Clients.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Dialogs Control Data
      dialogImage: false,
      selectedItemImage: null,
      dialogDetails: false,
      selectedAd: null,
      dialogDelete: false,
      itemToDelete: null,
      dialogToggle: false,
      itemToToggle: null,
      // End:: Dialogs Control Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
      // End:: Pagination Data
    };
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
    // End:: Page Query Param Watcher To Get Page Data Based On It's Change
  },

  methods: {
    // Start:: Handel Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/ads/all", query: { page: 1 } });
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.name = null;
      this.filterOptions.brand_name = null;
      this.filterOptions.number = null;
      this.filterOptions.city_id = null;
      this.filterOptions.status = null;
      this.filterOptions.payment_status = null;
      this.filterOptions.created_at = null;
      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/ads/all", query: { page: 1 } });
      }
      this.setTableRows();
    },
    // End:: Handel Filter

    // Start:: Set Table Rows
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top After Get Products
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "announcements",
          params: {
            page: this.paginations.current_page,
            name: this.filterOptions.name,
            brand_name: this.filterOptions.brand_name,
            number: this.filterOptions.number,
            city_id: this.filterOptions.city_id?.id,
            status: this.filterOptions.status?.value,
            payemnt_status: this.filterOptions.payment_status?.value,
            date: this.filterOptions.created_at,
          },
        });
        this.loading = false;
        this.tableRows = res.data.data.data;
        this.paginations.last_page = res.data.data.meta.last_page;
        this.paginations.items_per_page = res.data.data.meta.per_page;
      } catch (error) {
        this.loading = false;
        console.log(error.response.data.message);
      }
    },
    // End:: Set Table Rows

    // Start:: Fetch Cities
    async fetchCities() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: "cities?page=0&limit=0",
        });

        const citiesData = res.data.data.data || res.data.data;
        this.cities = [
          ...citiesData.map((city) => ({
            id: city.id,
            name: this.getAppLocale === "ar" ? city.name_ar : city.name_en,
            value: city.id,
          })),
        ];
      } catch (error) {
        console.log(error.response?.data?.message || error.message);
      }
    },
    // End:: Fetch Cities

    // Start:: Fetch Statistics
    async fetchStatistics() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: "announcement-statics",
        });

        this.statistics = res.data.data;
      } catch (error) {
        console.log(error.response?.data?.message || error.message);
      }
    },
    // End:: Fetch Statistics

    // Start:: Helper Methods
    formatDate(dateString) {
      if (!dateString) return this.$t("TABLES.noData");
      const date = new Date(dateString);
      return date.toLocaleDateString("ar-SA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },

    formatTime(dateString) {
      if (!dateString) return this.$t("TABLES.noData");
      const date = new Date(dateString);
      return date.toLocaleTimeString("ar-SA", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },

    calculateTotal(price, tax) {
      const priceNum = parseFloat(price) || 0;
      const taxNum = parseFloat(tax) || 0;
      return (priceNum + taxNum).toFixed(2);
    },

    calculateActualAttendance(ad) {
      // This would typically come from QR scan data
      // For now, using attendance_percantage to estimate
      if (ad.attendance_percantage && ad.guests_number) {
        return Math.round((ad.attendance_percantage / 100) * ad.guests_number);
      }
      return 0;
    },

    getAdStatusText(status) {
      const statuses = {
        published: this.$t("STATUS.published"),
        expired: this.$t("STATUS.done"),
        payment_failed: this.$t("STATUS.canceled"),
        stopped_by_admin: this.$t("STATUS.rejected"),
      };
      return statuses[status] || status || this.$t("TABLES.noData");
    },

    getPaymentStatusText(status) {
      const statuses = {
        successed: this.$t("STATUS.approved"),
        failed: this.$t("STATUS.rejected"),
        refunded: this.$t("STATUS.canceled"),
      };
      return statuses[status] || status || this.$t("TABLES.noData");
    },

    getPaymentMethodText(method) {
      const methods = {
        online: this.$t("STATUS.online"),
        cash: this.$t("STATUS.cash"),
        wallet: this.$t("PLACEHOLDERS.wallet"),
      };
      return methods[method] || method || this.$t("TABLES.noData");
    },
    // End:: Helper Methods

    // Start:: Modal Actions
    showImageModal(item) {
      this.dialogImage = true;
      this.selectedItemImage = item.image;
    },

    showDetails(item) {
      this.$router.push({ path: `/Ads/show/${item.id}` });
    },

    // Start:: Delete Methods
    selectDeleteItem(item) {
      this.dialogDelete = true;
      this.itemToDelete = item;
    },

    async confirmDeleteItem() {
      try {
        await this.$axios({
          method: "DELETE",
          url: `announcements/${this.itemToDelete.id}`,
        });

        this.dialogDelete = false;
        this.$message.success(this.$t("MESSAGES.deletedSuccessfully"));
        this.setTableRows();
        this.fetchStatistics();
      } catch (error) {
        this.dialogDelete = false;
        console.log(error.response?.data?.message || error.message);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Delete Methods

    // Start:: Toggle Status Methods
    selectToggleItem(item) {
      this.dialogToggle = true;
      this.itemToToggle = item;
    },

    async confirmToggleStatus() {
      try {
        await this.$axios({
          method: "GET",
          url: `announcements/toggle/${this.itemToToggle.id}`,
        });

        const successMessage =
          this.itemToToggle.status === "published"
            ? this.$t("MESSAGES.deactivatedSuccessfully")
            : this.$t("MESSAGES.activatedSuccessfully");

        this.dialogToggle = false;
        this.$message.success(successMessage);
        this.setTableRows();
        this.fetchStatistics();
      } catch (error) {
        this.dialogToggle = false;
        console.log(error.response?.data?.message || error.message);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Toggle Status Methods
    // End:: Modal Actions
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.fetchStatistics();
    this.fetchCities();
    this.setTableRows();
    // End:: Fire Methods
  },
};
</script>

<style lang="scss" scoped>
.disable_parent {
  cursor: no-drop !important;
}

.disable_child {
  opacity: 0.5;
  pointer-events: none !important;
}

// Start:: Statistics Cards Styles
.statistics_cards_wrapper {
  margin-top: 20px;

  .stat_card {
    background: #fff;
    border-radius: 12px;
    padding: 20px;
    display: flex;
    align-items: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
    height: 100%;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
    }

    .stat_icon {
      width: 60px;
      height: 60px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: 15px;
      flex-shrink: 0;

      i {
        font-size: 26px;
        color: white;
      }

      &.total {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      }

      &.published {
        background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
      }

      &.finished {
        background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
      }

      &.failed {
        background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
      }
    }

    .stat_content {
      flex: 1;

      h4 {
        font-size: 32px;
        font-weight: bold;
        color: #333;
        margin: 0 0 5px 0;
        line-height: 1;
      }

      p {
        margin: 0;
        color: #666;
        font-size: 14px;
        font-weight: 500;
      }
    }
  }
}
// End:: Statistics Cards Styles

.badge {
  padding: 5px 12px;
  border-radius: 5px;
  color: white;
  font-weight: bold;
  display: inline-block;
  font-size: 12px;
}

.badge-success {
  background-color: #28a745;
}

.badge-danger {
  background-color: #dc3545;
}

.badge-warning {
  background-color: #ffc107;
  color: #000;
}

.badge-info {
  background-color: #17a2b8;
}

.details-section {
  .section-title {
    color: #f7941e;
    font-weight: bold;
    border-bottom: 2px solid #f7941e;
    padding-bottom: 8px;
    margin-top: 20px;
  }

  .detail-item {
    padding: 8px 0;
    border-bottom: 1px solid #eee;

    strong {
      display: inline-block;
      min-width: 150px;
      color: #333;
      margin-left: 10px;
    }

    span {
      color: #666;
    }
  }

  .date-item {
    padding: 10px;
    background: #f8f9fa;
    border-radius: 5px;
    margin-bottom: 10px;
  }

  .ad-image {
    width: 100%;
    height: 200px;
    object-fit: cover;
    border-radius: 8px;
    cursor: pointer;
    transition: transform 0.3s;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    &:hover {
      transform: scale(1.05);
    }
  }

  .map-container {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

::v-deep .v-dialog {
  .v-card__title {
    background: linear-gradient(135deg, #f7941e 0%, #ff8c00 100%);
  }
}

.transparent-bg {
  background-color: transparent !important;
  background:none !important;
}
</style>
